import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  contractTemplatesApi,
  ContractType,
  CreateContractTemplateDto,
  UpdateContractTemplateDto,
} from '../../../api/contracts';
import { notifications } from '@mantine/notifications';

export const useContractTemplates = (type?: ContractType) => {
  return useQuery({
    queryKey: ['contractTemplates', type],
    queryFn: () => contractTemplatesApi.list(type),
  });
};

export const useContractTemplate = (id: string) => {
  return useQuery({
    queryKey: ['contractTemplate', id],
    queryFn: () => contractTemplatesApi.get(id),
    enabled: !!id,
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContractTemplateDto) => contractTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] });
      notifications.show({
        title: 'Success',
        message: 'Template created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create template',
        color: 'red',
      });
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContractTemplateDto }) =>
      contractTemplatesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['contractTemplate', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Template updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update template',
        color: 'red',
      });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contractTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] });
      notifications.show({
        title: 'Success',
        message: 'Template deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete template',
        color: 'red',
      });
    },
  });
};

export const useDefaultTemplate = (type: ContractType) => {
  return useQuery({
    queryKey: ['defaultTemplate', type],
    queryFn: () => contractTemplatesApi.getDefault(type),
    enabled: !!type,
  });
};

